import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { MainLayout } from './layouts/MainLayout';
import Home from './pages/Home';
import Documents from './pages/Documents';
import Upload from './pages/Upload';
import ServerStatusPage from './pages/ServerStatusPage';
import DocumentView from './pages/DocumentView';
import ReconstructedDocumentView from './pages/ReconstructedDocumentView';
import ReconstructedDocuments from './pages/ReconstructedDocuments';
import Login from './pages/Login';
import DocumentPages from './pages/DocumentPages';
import DocumentPageView from './pages/DocumentPageView';
import { AuthRedirect } from './components/AuthRedirect';
import Audit from './pages/Audit';
import Categories from './pages/Categories';
import CategoryView from './pages/CategoryView';
import CategoryClusterView from './pages/CategoryClusterView';
import Test from './pages/Test';
import { PlatformDataProvider } from './hooks/usePlatformData';
import AuthGuard from './components/AuthGuard';
import { FaTools } from 'react-icons/fa';
import CategoryCreate from './pages/CategoryCreate';
import ClassificationQuality from './pages/ClassificationQuality';
import Debugger from './pages/Debugger';
import Uploads from './pages/Uploads';
import UploadView from './pages/UploadView';
import Supertypes from './pages/Supertypes';
import SupertypeView from './pages/SupertypeView';
import Projects from './pages/Projects';
import ProjectView from './pages/ProjectView';
import ProjectGraphRoute from './pages/projectGraph/ProjectGraphRoute';
import Organisations from './pages/Organisations';
import OrganisationView from './pages/OrganisationView';
import Users from './pages/Users/Users';
import Security from './pages/Security';
import UndeliveredEvents from './pages/undeliveredEvents/UndeliveredEvents';
import ChatQuestions from './pages/chatQuestions/ChatQuestions';
import Monitoring from './pages/monitoring/Monitoring';
import Curation from './pages/curation/Curation';
import CurationTable from './pages/curation/CurationTable';
import Leaderboard from './pages/curation/Leaderboard';
import ReviewHistory from './pages/curation/ReviewHistory';
import SupervisorAudit from './pages/supervisorAudit/SupervisorAudit';
import { homeRedirect } from './lib/homeRedirect';
import { AuthUnauthorizedListener } from './components/AuthUnauthorizedListener';

export const AppRoutes = () => {
  return (
    <>
      <AuthUnauthorizedListener />
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />

      {/* Protected routes */}
      <Route
        element={
          <AuthProvider>
            <PlatformDataProvider>
              <MainLayout />

              <AuthGuard roles={[]}>
                <MaintenanceMode />
              </AuthGuard>
            </PlatformDataProvider>
          </AuthProvider>
        }
      >
        <Route
          path="/"
          element={
            <AuthRedirect condition={homeRedirect}>
              <Home />
            </AuthRedirect>
          }
        />
        <Route path="/documents" element={<Documents />} />
        <Route path="/document-pages" element={<DocumentPages />} />
        <Route path="/document-pages/:id" element={<DocumentPageView />} />
        <Route path="/documents/upload" element={<Upload />} />
        <Route path="/documents/:id" element={<DocumentView />} />
        <Route path="/reconstructed-documents" element={<ReconstructedDocuments />} />
        <Route path="/reconstructed-documents/:id" element={<ReconstructedDocumentView />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/classification-quality" element={<ClassificationQuality />} />
        <Route path="/categories/new" element={<CategoryCreate />} />
        <Route path="/categories/:id" element={<CategoryView />} />
        <Route path="/category-clusters/:id" element={<CategoryClusterView />} />
        <Route path="/status" element={<ServerStatusPage />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/test" element={<Test />} />
        <Route path="/debugger" element={<Debugger />} />
        <Route path="/uploads" element={<Uploads />} />
        <Route path="/uploads/:id" element={<UploadView />} />
        <Route path="/supertypes" element={<Supertypes />} />
        <Route path="/supertypes/:id" element={<SupertypeView />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectView />} />
        <Route path="/projects/:id/graph" element={<ProjectGraphRoute />} />
        <Route path="/organisations" element={<Organisations />} />
        <Route path="/organisations/:id" element={<OrganisationView />} />
        <Route
          path="/users"
          element={
            <AuthGuard role="admin" fallback={<Navigate to="/" replace />}>
              <Users />
            </AuthGuard>
          }
        />
        <Route path="/security" element={<Security />} />
        <Route
          path="/undelivered-events"
          element={
            <AuthGuard role="admin" fallback={<Navigate to="/" replace />}>
              <UndeliveredEvents />
            </AuthGuard>
          }
        />
        <Route
          path="/chat-questions"
          element={
            <AuthGuard role="admin" fallback={<Navigate to="/" replace />}>
              <ChatQuestions />
            </AuthGuard>
          }
        />
        <Route path="/curation" element={<Curation />} />
        <Route path="/curation/list" element={<CurationTable />} />
        <Route path="/curation/leaderboard" element={<Leaderboard />} />
        <Route path="/curation/history" element={<ReviewHistory />} />
        <Route
          path="/monitoring"
          element={
            <AuthGuard roles={['admin', 'curator']} fallback={<Navigate to="/" replace />}>
              <Monitoring />
            </AuthGuard>
          }
        />
        <Route
          path="/supervisor/audit"
          element={
            <AuthGuard roles={['admin', 'supervisor', 'curator']} fallback={<Navigate to="/" replace />}>
              <SupervisorAudit />
            </AuthGuard>
          }
        />
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
};

function MaintenanceMode() {
  return (
    <div className="fixed inset-0 bg-white dark:bg-black z-50">
      <div className="flex flex-col items-center justify-center h-full">
        <h1 className="text-4xl font-bold flex items-center gap-2 mb-4">
          <FaTools />
          Maintenance Mode
        </h1>
        <p className="text-lg text-gray-500 dark:text-zinc-400">
          We are currently performing maintenance on the system. Please check back later.
        </p>
      </div>
    </div>
  );
}
